import {
  SourceSettings,
  VehicleSettings,
} from '../admin/Settings/SourceSettings/SourceSettingsCard/Components/types';
import { GOODSAM_SOURCE_ID } from '../constants';
import { Loan, LoanPurpose, LoanType } from '../schema';

// Fallback minimal financed amounts if no config is available
const MINIMAL_FINANCED_AMOUNT: { [_key: string]: any } = {
  default: {
    '': {
      rv: 10000,
      boat: 25000,
      aircraft: 50000,
    },
    new: {
      rv: 10000,
      boat: 25000,
      aircraft: 50000,
    },
    used: {
      rv: 10000,
      boat: 25000,
      aircraft: 50000,
    },
    refinance: {
      rv: 50000,
      boat: 50000,
      aircraft: 50000,
    },
    cashrecapture: {
      rv: 10000,
      boat: 25000,
      aircraft: 50000,
    },
  },
  goodSam: {
    boat: 25000,
    rv: 10000,
  },
};

function getMinAmountFromSourceConfig(
  sourceConfig: SourceSettings,
  purpose: string,
  loanType: string | null
): number | null {
  // Map loan type string to enum
  const loanTypeMap: { [key: string]: LoanType } = {
    new: LoanType.new,
    used: LoanType.used,
    refinance: LoanType.refinance,
    cashrecapture: LoanType.cashRecapture,
  };

  const mappedLoanType = loanTypeMap[(loanType || '').toLowerCase()];

  // Map purpose string to enum value
  const purposeMap: { [key: string]: string } = {
    boat: LoanPurpose.boat,
    rv: LoanPurpose.rv,
    aircraft: LoanPurpose.aircraft,
  };

  const mappedPurpose = purposeMap[purpose.toLowerCase()] || purpose;
  const purposeConfig = sourceConfig[mappedPurpose as keyof SourceSettings] as
    | VehicleSettings
    | undefined;

  // If there is no specific configuration for this source, purpose, and loan type, return null
  if (!purposeConfig?.[mappedLoanType]) {
    return null;
  }

  // If we found a specific configuration for this source, purpose, and loan type,
  // and the minLoanAmount is null or undefined, treat both as 0
  if (purposeConfig?.[mappedLoanType]?.minLoanAmount == null) {
    return 0;
  }

  return purposeConfig[mappedLoanType]?.minLoanAmount ?? null;
}

function getMinAmountFromConfig(loan: Loan, config?: any): number | null {
  if (!config?.settings?.sources?.list?.length) {
    return null;
  }

  const { sources } = config.settings;
  const { purpose, source, loanType } = loan;

  // Step 1: Check for a specific source configuration
  const foundSource = sources.list.find((s: SourceSettings) => s.id === source);

  if (foundSource) {
    const result = getMinAmountFromSourceConfig(foundSource, purpose, loanType);
    if (result !== null) {
      return result;
    }
  }

  // Step 2: If no specific source found or no config for that source, check for default source
  if (sources.defaultLocator && sources.list[sources.defaultLocator.indexOfDefault]) {
    const defaultSource = sources.list[sources.defaultLocator.indexOfDefault];

    const result = getMinAmountFromSourceConfig(defaultSource, purpose, loanType);
    if (result !== null) {
      return result;
    }
  }

  // Step 3: No specific or default source configuration found
  return null;
}

function getMinAmountFromHardcoded(loan: Loan): number {
  const GOOD_SAM = 'goodSam';
  const provider = loan.source === GOODSAM_SOURCE_ID ? GOOD_SAM : 'default';
  const loanType = (loan?.loanType || '')?.toLowerCase();
  const purpose = loan.purpose.toLowerCase();

  return provider === GOOD_SAM
    ? MINIMAL_FINANCED_AMOUNT.goodSam[purpose]
    : MINIMAL_FINANCED_AMOUNT[provider][loanType][purpose];
}

export function getMinAllowedAmount(loan: Loan, config?: any): number {
  // Try to get minimum amount from config first
  const configAmount = getMinAmountFromConfig(loan, config);

  if (configAmount !== null) {
    return configAmount;
  }

  return getMinAmountFromHardcoded(loan);
}
